/**
 * Swamithra — an Expert's response draft on a query or a vetting request.
 *
 * One draft per request, shared by every Expert; see ../domain/response-draft
 * for the rules. A save is a PUT that replaces the draft, quoting the version
 * that was loaded. No idempotency key: a retried save quotes a version the first
 * attempt already moved past, so the API refuses it as `draft_conflict` rather
 * than applying it twice.
 *
 * The keep list travels as ONE comma-separated field, not a repeated one: the
 * API's idempotency layer accepts each multipart field exactly once, and the
 * send routes it shares with are behind that layer.
 */

import { referenceParam } from "../domain/ids"
import {
	responseDraftSchema,
	type SaveResponseDraftInput,
	saveResponseDraftInputSchema,
	type SendFromDraft,
	sendFromDraftSchema,
} from "../domain/response-draft"
import { appendFiles, type PlatformFile } from "../files"
import { api } from "./client"
import { type SubmitOptions } from "./options"

/** Saves the query's response draft. */
export function saveExpertQueryDraft(
	queryRef: string,
	input: SaveResponseDraftInput,
	attachments: readonly PlatformFile[],
	options: Pick<SubmitOptions, "signal"> = {},
) {
	return saveDraft(
		`/expert/queries/${referenceParam(queryRef)}/draft`,
		input,
		attachments,
		options,
	)
}

/** Saves the vetting request's response draft. */
export function saveExpertVettingDraft(
	vettingRef: string,
	input: SaveResponseDraftInput,
	attachments: readonly PlatformFile[],
	options: Pick<SubmitOptions, "signal"> = {},
) {
	return saveDraft(
		`/expert/vetting-requests/${referenceParam(vettingRef)}/draft`,
		input,
		attachments,
		options,
	)
}

async function saveDraft(
	path: string,
	input: SaveResponseDraftInput,
	attachments: readonly PlatformFile[],
	options: Pick<SubmitOptions, "signal">,
) {
	const parsed = saveResponseDraftInputSchema.parse(input)
	const form = new FormData()
	form.append("body_rich", parsed.bodyRich)
	form.append("base_version", String(parsed.baseVersion))
	form.append("draft_attachment_ids", parsed.keepAttachmentIds.join(","))
	appendFiles(form, "attachments", attachments)
	return await api.put(path, responseDraftSchema, form, { signal: options.signal })
}

/**
 * Adds the "sent from a draft" fields to a reply's form. Nothing is added for a
 * reply that was not written from the saved draft; the API then discards any
 * draft on the request.
 */
export function appendDraftSend(form: FormData, fromDraft: SendFromDraft | undefined): void {
	if (!fromDraft) return
	const parsed = sendFromDraftSchema.parse(fromDraft)
	form.append("draft_version", String(parsed.version))
	form.append("draft_attachment_ids", parsed.keepAttachmentIds.join(","))
}
